import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collageApi } from '../../api/client';
import type { Collage } from '../../types';
import { resolveTheme } from '../../utils/collageThemes';

export function CollagesListPage() {
  const [collages, setCollages] = useState<Collage[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareUrls, setShareUrls] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    setLoading(true);
    collageApi.list().then(setCollages).finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this collage? The photos it uses stay in your archive — only the arrangement is removed.')) return;
    setBusyId(id);
    await collageApi.remove(id);
    setCollages(prev => prev.filter(c => c.id !== id));
    setBusyId(null);
  }

  async function handleShare(id: string) {
    setBusyId(id);
    const { publicUrl } = await collageApi.publish(id);
    setShareUrls(prev => ({ ...prev, [id]: publicUrl }));
    setCollages(prev => prev.map(c => c.id === id ? { ...c, isPublic: true } : c));
    setBusyId(null);
  }

  async function handleUnshare(id: string) {
    setBusyId(id);
    await collageApi.unpublish(id);
    setShareUrls(prev => { const next = { ...prev }; delete next[id]; return next; });
    setCollages(prev => prev.map(c => c.id === id ? { ...c, isPublic: false } : c));
    setBusyId(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xl">🖼️</span>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">My Collages</h1>
        </div>
        <Link
          to="/events/collages/new"
          className="px-3 lg:px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-md shadow-indigo-200 transition-colors"
        >
          + New Collage
        </Link>
      </div>
      <p className="text-slate-500 text-sm mb-6">Turn photos from one or more events into a shareable grid or storyboard.</p>

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : collages.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-indigo-200">
          <p className="text-slate-500 font-medium">No collages yet</p>
          <p className="text-slate-400 text-sm mt-1">Pick photos across your events and arrange them into something shareable</p>
          <Link to="/events/collages/new" className="inline-block mt-5 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-200">
            🖼️ Create a Collage
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {collages.map(c => {
            const theme = resolveTheme(c.themeId);
            const firstPhoto = c.items[0]?.eventPhoto;
            return (
              <div key={c.id} data-testid="collage-card" className="bg-white rounded-2xl border border-indigo-50 shadow-sm overflow-hidden flex flex-col">
                <div className="h-32 bg-indigo-50 flex items-center justify-center overflow-hidden">
                  {c.renderedImageUrl ? (
                    <img src={c.renderedImageUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                  ) : firstPhoto ? (
                    <img src={firstPhoto.filePath} alt="" loading="lazy" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl">🖼️</span>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h2 className="font-serif font-bold text-slate-900 mb-0.5 truncate">{c.title || 'Untitled Collage'}</h2>
                  <p className="text-xs text-slate-500 mb-2">
                    {c.type === 'grid' ? 'Grid' : 'Storyboard'} &middot; {theme.name} &middot; {c.items.length} photo{c.items.length === 1 ? '' : 's'}
                  </p>

                  {shareUrls[c.id] && (
                    <div className="mb-3 flex items-center gap-1.5">
                      <input readOnly value={shareUrls[c.id]} className="flex-1 min-w-0 text-xs border border-emerald-200 rounded-lg px-2 py-1 bg-emerald-50" />
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(shareUrls[c.id])}
                        className="px-2 py-1 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 flex-shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                  )}

                  <div className="mt-auto flex items-center gap-1.5 flex-wrap text-xs">
                    <Link
                      to={`/events/collages/${c.id}`}
                      className="px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                    >
                      ▶ View
                    </Link>
                    <Link
                      to={`/events/collages/${c.id}/edit`}
                      className="px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
                    >
                      ✏️ Edit
                    </Link>
                    {c.isPublic ? (
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => handleUnshare(c.id)}
                        className="px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
                      >
                        🔗 Unshare
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => handleShare(c.id)}
                        className="px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
                      >
                        🔗 Share
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busyId === c.id}
                      onClick={() => handleDelete(c.id)}
                      className="px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
